import { Txt } from "@stenajs-webui/core";
import { type FC, Suspense, useEffect } from "react";

import { CargoGoodsRadioButtons } from "../../../../../common-ui/cargo/CargoGoodsRadioButtons";
import { GoodsDescriptionsEnsHintBanner } from "../../../../../common-ui/goods/GoodsDescriptionsEnsHintBanner";
import { Loading } from "../../../../../common-ui/Loading";
import { useShowEnsGoodsBanner } from "../../../../../common/goods/useGoods";
import type { BasicBooking } from "../../../../../common/graphql/fragments/gql/BasicBooking";
import { useRecentBookingsBackgroundQuery } from "../../../../../common/graphql/useRecentBookings";
import { useAppNavigate } from "../../../../../common/hooks/useAppNavigate";
import { Information } from "../../../../../common/texts";
import { useUpdateField } from "../../../../../common/update-information/hooks/useUpdateField";
import {
  type BookingFields,
  isAnyVisible,
  isHidden,
} from "../../../../../common/utils/bookingFields";
import { useUpdateFormStateMinimalBooking } from "../../../../../common/utils/minimalBooking";
import { ColumnId } from "../../../../../gql/graphql";
import { useGoodsTypesBackgroundQuery } from "../../../../../services/intermodal/hooks/useGoodsTypes";
import type { BookingDetailsLocationState } from "../../utils/detailsUtils";
import { BookingSection } from "./BookingSection";
import { CargoDetailsSection } from "./CargoDetailsSection";
import { GoodsDetailsSection } from "./GoodsDetailsSection";

interface CargoGoodsDetailsSectionProps {
  booking: BasicBooking;
  disabled: boolean;
  fieldsToEnable: BookingFields;
  fieldsToShow: BookingFields;
}

export const CargoGoodsDetailsSection: FC<CargoGoodsDetailsSectionProps> = ({
  fieldsToShow,
  fieldsToEnable,
  disabled,
  booking,
}) => {
  const { editedValue: empty, onChange: onChangeEmpty } =
    useUpdateField("empty");
  const recentBookingsQueryRef = useRecentBookingsBackgroundQuery();
  const goodsTypesQueryRef = useGoodsTypesBackgroundQuery();

  const showCargoSection = isAnyVisible(fieldsToShow, [
    ColumnId.LivestockTypeCode,
    ColumnId.HazardousGoods,
    ColumnId.CargoWeight,
    ColumnId.Temperature,
    ColumnId.SenderCountryCode,
    ColumnId.ReceiverCountryCode,
  ]);

  const showGoodsSection = isAnyVisible(fieldsToShow, [ColumnId.Goods]);

  const minimalBooking = useUpdateFormStateMinimalBooking();
  const showEnsGoodsBanner = useShowEnsGoodsBanner(minimalBooking, {
    ensConfirmed: booking.importReference.ensConfirmed,
  });

  const fullyDisabled =
    disabled ||
    (isHidden(fieldsToEnable, ColumnId.Goods) &&
      isHidden(fieldsToEnable, ColumnId.Temperature) &&
      isHidden(fieldsToEnable, ColumnId.HazardousGoods) &&
      isHidden(fieldsToEnable, ColumnId.LivestockTypeCode));

  const isEmpty = Boolean(empty);

  const { locationState } = useAppNavigate<BookingDetailsLocationState>();

  useEffect(() => {
    if (locationState.addEmptyGoodsItem && isEmpty && !fullyDisabled) {
      onChangeEmpty(false);
    }
  }, [locationState.addEmptyGoodsItem, isEmpty, fullyDisabled, onChangeEmpty]);

  return (
    <BookingSection title={"Cargo"}>
      {!showCargoSection && !showGoodsSection ? (
        <Txt>{Information.NoCargoOptions}</Txt>
      ) : (
        <>
          {showEnsGoodsBanner && <GoodsDescriptionsEnsHintBanner />}
          <CargoGoodsRadioButtons
            disabled={fullyDisabled}
            value={isEmpty}
            onChange={onChangeEmpty}
          />
          {showCargoSection && !empty && (
            <CargoDetailsSection
              fieldsToShow={fieldsToShow}
              fieldsToEnable={fieldsToEnable}
              disabled={disabled}
              booking={booking}
            />
          )}
          {showGoodsSection && !empty && (
            <Suspense fallback={<Loading />}>
              <GoodsDetailsSection
                booking={booking}
                fieldsToShow={fieldsToShow}
                fieldsToEnable={fieldsToEnable}
                disabled={disabled}
                recentBookingsQueryRef={recentBookingsQueryRef}
                goodsTypesQueryRef={goodsTypesQueryRef}
              />
            </Suspense>
          )}
        </>
      )}
    </BookingSection>
  );
};
